
import React from "react";
import {
  CheckCircle2,
  ArrowRight,
  ArrowUpRight,
  DraftingCompass,
  Building2,
  ClipboardCheck,
  Paintbrush,
  PaintBucket,
  ShieldCheck,
  BadgeCheck,
} from "lucide-react";

const services = [
  {
    id: "01",
    title: "Interior Design",
    category: "CUBE4SPACES",
    description:
      "We design beautiful and practical spaces that match your style, needs and budget.",
    features: [
      "Home and office interiors",
      "Modular kitchens and wardrobes",
      "Space planning and 3D designs",
      "Custom furniture and lighting",
    ],
    icon: DraftingCompass,
    href: "/interior-design-coimbatore/",
    accent: "indigo",
  },
  {
    id: "02",
    title: "Construction",
    category: "CUBE4SPACES",
    description:
      "We help build residential and commercial spaces with proper planning, quality materials and site supervision.",
    features: [
      "Residential construction",
      "Commercial construction",
      "Site supervision",
      "Material selection and work coordination",
    ],
    icon: Building2,
    href: "/construction-company-coimbatore/",
    accent: "indigo",
  },
  {
    id: "03",
    title: "Complete Project Solutions",
    category: "DESIGN & BUILD",
    description:
      "Get design, construction, interiors and painting services managed together by one coordinated team.",
    features: [
      "Design and construction",
      "Project planning and coordination",
      "Interior and painting work",
      "Final checks and handover",
    ],
    icon: ClipboardCheck,
    href: "/complete-project-solutions-coimbatore/",
    accent: "indigo",
  },
  {
    id: "04",
    title: "Interior Painting",
    category: "PAINT STUDIO",
    description:
      "Give your home or office a fresh look with suitable colours and quality painting finishes.",
    features: [
      "Living room and bedroom painting",
      "Kitchen and ceiling painting",
      "New home painting and repainting",
      "Decorative and texture painting",
    ],
    icon: Paintbrush,
    href: "/paint-studio-coimbatore/#interior-painting",
    accent: "amber",
  },
  {
    id: "05",
    title: "Exterior Painting",
    category: "PAINT STUDIO",
    description:
      "Protect and refresh your property's exterior with painting solutions for different buildings and surfaces.",
    features: [
      "House and villa painting",
      "Apartment and commercial building painting",
      "Compound wall painting",
      "Exterior wall repainting",
    ],
    icon: PaintBucket,
    href: "/paint-studio-coimbatore/#exterior-painting",
    accent: "amber",
  },
  {
    id: "06",
    title: "Waterproofing & Special Finishes",
    category: "PAINT STUDIO",
    description:
      "Protect your property and improve its appearance with suitable surface protection and decorative finishes.",
    features: [
      "Waterproofing solutions",
      "Water-damage surface repair",
      "Wall texture and decorative painting",
      "Door, wood and furniture painting",
    ],
    icon: ShieldCheck,
    href: "/paint-studio-coimbatore/#waterproofing",
    accent: "amber",
  },
];

const benefits = [
  {
    title: "Clear Project Planning",
    description:
      "Understand the scope, priorities and next steps before work begins.",
  },
  {
    title: "Careful Material Selection",
    description:
      "Choose materials and finishes suited to your design and project requirements.",
  },
  {
    title: "Regular Site Coordination",
    description:
      "Keep project activities coordinated from planning through execution.",
  },
  {
    title: "Design, Build & Paint",
    description:
      "Bring interior design, construction and painting services together in one place.",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      aria-labelledby="services-heading"
      className="relative isolate w-full overflow-hidden bg-slate-50 py-16 font-['DM_Sans',sans-serif] text-slate-900 sm:py-20 md:py-24 lg:py-28 xl:py-32"
    >
      {/* Background decoration */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_left,rgba(99,102,241,0.08),transparent_42%),radial-gradient(ellipse_at_bottom_right,rgba(245,158,11,0.06),transparent_38%)]"
      />

      <div className="mx-auto w-full max-w-[1440px] px-5 sm:px-8 md:px-10 lg:px-12 xl:px-16 2xl:px-20">
        {/* SECTION HEADER */}
        <div className="mb-14 grid grid-cols-1 items-end gap-8 md:mb-16 lg:grid-cols-[minmax(0,1.4fr)_minmax(280px,0.6fr)] lg:gap-14 xl:mb-20">
          <div>
            <span className="mb-6 inline-flex items-center gap-2.5 rounded-full border border-indigo-100 bg-indigo-50 px-4 py-2 font-['Poppins',sans-serif] text-[10px] font-semibold uppercase tracking-[0.16em] text-indigo-700 sm:mb-7 sm:px-5 sm:text-xs">
              <span className="size-2 rounded-full bg-indigo-600" />
              Our Services
            </span>

            <h2
              id="services-heading"
              className="max-w-4xl font-['Poppins',sans-serif] text-[clamp(2.4rem,5.6vw,5.25rem)] font-semibold leading-[1.1] tracking-[-0.06em] text-slate-950"
            >
              Design. Build.
              <br />
              <span className="text-indigo-600">Paint.</span>
            </h2>

            <p className="mt-6 max-w-3xl text-sm leading-7 text-slate-600 sm:mt-7 sm:text-base sm:leading-8 md:text-lg">
              Cube4Spaces and Paint Studio help you create beautiful,
              comfortable and well-finished spaces. From home interiors
              and construction to wall painting and waterproofing, we
              take care of your space from the first plan to the final
              finish.
            </p>

            {/* Brand identities */}
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-3">
              <div>
                <p className="font-['Poppins',sans-serif] text-sm font-semibold text-slate-900 sm:text-base">
                  Cube4Spaces
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  Interior Design & Construction
                </p>
              </div>

              <span className="text-xl font-light text-indigo-400">
                ×
              </span>

              <div>
                <p className="font-['Poppins',sans-serif] text-sm font-semibold text-slate-900 sm:text-base">
                  Paint Studio
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  Interior & Exterior Painting
                </p>
              </div>
            </div>
          </div>

          {/* Project solutions panel */}
          <div className="relative overflow-hidden rounded-2xl bg-slate-950 p-6 text-white shadow-xl shadow-slate-900/10 sm:rounded-3xl sm:p-8 lg:p-7 xl:p-9">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -right-12 -top-12 size-40 rounded-full bg-indigo-500/20 blur-3xl"
            />

            <div className="relative">
              <div className="mb-6 flex size-12 items-center justify-center rounded-xl border border-white/10 bg-white/[0.08] text-indigo-300 sm:size-14">
                <BadgeCheck size={27} strokeWidth={1.7} />
              </div>

              <span className="font-['Poppins',sans-serif] text-[10px] font-medium uppercase tracking-[0.17em] text-indigo-300">
                Complete Project Solutions
              </span>

              <h3 className="mt-4 font-['Poppins',sans-serif] text-xl font-semibold leading-snug tracking-tight sm:text-2xl xl:text-3xl">
                From the first plan to the final finish.
              </h3>

              <p className="mt-4 text-sm leading-7 text-slate-300 sm:text-base sm:leading-8">
                From planning and design to construction, painting and
                final handover, our team brings everything together
                to make your project easier.
              </p>

              <div className="mt-6 flex items-center gap-3 border-t border-white/10 pt-5">
                <span className="size-2 rounded-full bg-emerald-400" />
                <span className="text-xs font-medium text-slate-300 sm:text-sm">
                  One coordinated approach to your space
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* SERVICES GRID */}
        <div className="mb-8 flex flex-col gap-3 sm:mb-10 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h3 className="font-['Poppins',sans-serif] text-xl font-semibold tracking-tight text-slate-950 sm:text-2xl">
              What We Do
            </h3>
            <p className="mt-2 text-sm leading-6 text-slate-500 sm:text-base">
              Six specialist services for your complete space.
            </p>
          </div>

          <span className="self-start rounded-full border border-slate-200 bg-white px-3.5 py-2 text-xs font-medium text-slate-500 sm:self-auto">
            06 Service Categories
          </span>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6 xl:grid-cols-3 xl:gap-7">
          {services.map((service) => {
            const Icon = service.icon;
            const isPainting = service.accent === "amber";

            return (
              <article
                key={service.id}
                className="group flex min-w-0 flex-col rounded-2xl border border-slate-200/90 bg-white p-5 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-indigo-200 hover:shadow-xl hover:shadow-slate-900/[0.06] sm:rounded-3xl sm:p-7 xl:p-8"
              >
                {/* Card top */}
                <div className="mb-6 flex items-start justify-between gap-4">
                  {/* Updated icon style only */}
                  <div className="flex size-12 shrink-0 items-center justify-center sm:size-14">
                    <Icon
                      size={28}
                      strokeWidth={1.3}
                      className="text-slate-700"
                      aria-hidden="true"
                    />
                  </div>

                  <span className="font-['Poppins',sans-serif] text-3xl font-semibold tracking-tight text-slate-100 transition-colors group-hover:text-indigo-100 sm:text-4xl">
                    {service.id}
                  </span>
                </div>

                {/* Category label */}
                <span
                  className={`mb-3 self-start font-['Poppins',sans-serif] text-[9px] font-semibold tracking-[0.16em] ${
                    isPainting ? "text-amber-700" : "text-indigo-700"
                  }`}
                >
                  {service.category}
                </span>

                <h3 className="font-['Poppins',sans-serif] text-xl font-semibold leading-snug tracking-tight text-slate-950 sm:text-2xl">
                  {service.title}
                </h3>

                <p className="mt-4 text-sm leading-7 text-slate-600 sm:text-[15px] sm:leading-7">
                  {service.description}
                </p>

                {/* Features */}
                <ul className="mt-6 flex-1 space-y-3 border-t border-slate-100 pt-6">
                  {service.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-start gap-3"
                    >
                      <CheckCircle2
                        size={17}
                        strokeWidth={1.9}
                        className={`mt-0.5 shrink-0 ${
                          isPainting
                            ? "text-amber-600"
                            : "text-indigo-600"
                        }`}
                      />

                      <span className="text-sm leading-6 text-slate-700">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Internal link */}
                <a
                  href={service.href}
                  className="mt-7 inline-flex min-h-11 items-center justify-between gap-3 border-t border-slate-100 pt-5 font-['Poppins',sans-serif] text-xs font-semibold text-slate-800 transition-colors hover:text-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-indigo-600"
                  aria-label={`Explore ${service.title} services`}
                >
                  <span>Explore Service</span>

                  <ArrowUpRight
                    size={18}
                    strokeWidth={1.8}
                    className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  />
                </a>
              </article>
            );
          })}
        </div>

        {/* WHY CHOOSE US */}
        <div className="mt-20 grid grid-cols-1 items-start gap-10 border-t border-slate-200 pt-14 sm:mt-24 sm:pt-16 lg:mt-28 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-center lg:gap-14 xl:gap-20">
          <div>
            <span className="font-['Poppins',sans-serif] text-[10px] font-semibold uppercase tracking-[0.17em] text-indigo-700 sm:text-xs">
              Why Choose Us
            </span>

            <h3 className="mt-5 max-w-xl font-['Poppins',sans-serif] text-[clamp(2rem,4vw,3.75rem)] font-semibold leading-[1.14] tracking-[-0.05em] text-slate-950">
              From Start to Finish,
              <span className="text-indigo-600">
                {" "}We Make It Easier.
              </span>
            </h3>

            <p className="mt-5 max-w-xl text-sm leading-7 text-slate-600 sm:mt-6 sm:text-base sm:leading-8">
              Cube4Spaces and Paint Studio bring interior design,
              construction and painting services together to help you
              manage your project with confidence.
            </p>

            {/* CTA buttons */}
            <div className="mt-7 flex flex-col gap-3 sm:mt-9 sm:flex-row sm:flex-wrap">
              <a
                href="/contact/"
                className="group inline-flex min-h-12 items-center justify-center gap-4 rounded-lg bg-slate-950 px-5 py-3 font-['Poppins',sans-serif] text-xs font-semibold text-white transition duration-300 hover:-translate-y-0.5 hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-indigo-600 sm:min-h-14 sm:px-6 sm:text-sm"
              >
                <span>Start Your Project</span>

                <ArrowRight
                  size={18}
                  className="transition-transform group-hover:translate-x-1"
                />
              </a>

              <a
                href="/paint-studio-coimbatore/"
                className="inline-flex min-h-12 items-center justify-center gap-3 rounded-lg border border-slate-300 bg-white px-5 py-3 font-['Poppins',sans-serif] text-xs font-semibold text-slate-800 transition duration-300 hover:border-indigo-300 hover:bg-indigo-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-indigo-600 sm:min-h-14 sm:px-6 sm:text-sm"
              >
                <Paintbrush size={17} />
                <span>Explore Painting Services</span>
              </a>
            </div>
          </div>

          {/* Benefit cards */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
            {benefits.map((benefit, index) => (
              <div
                key={benefit.title}
                className="flex min-w-0 items-start gap-4 rounded-xl border border-slate-200 bg-white p-5 transition duration-300 hover:border-indigo-200 hover:shadow-lg hover:shadow-indigo-950/[0.04] sm:rounded-2xl sm:p-6"
              >
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-700">
                  <CheckCircle2 size={21} strokeWidth={1.9} />
                </div>

                <div className="min-w-0">
                  <span className="font-['Poppins',sans-serif] text-[9px] font-semibold tracking-[0.13em] text-indigo-600">
                    0{index + 1}
                  </span>

                  <h4 className="mt-1 font-['Poppins',sans-serif] text-sm font-semibold leading-6 text-slate-900 sm:text-base">
                    {benefit.title}
                  </h4>

                  <p className="mt-2 text-xs leading-6 text-slate-500 sm:text-sm">
                    {benefit.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FINAL CTA */}
        <div className="relative mt-16 overflow-hidden rounded-2xl bg-indigo-600 px-6 py-9 text-white sm:mt-20 sm:rounded-3xl sm:px-10 sm:py-12 lg:mt-24 lg:px-14 lg:py-14">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-16 -top-24 size-64 rounded-full border border-white/10 sm:size-80"
          />

          <div
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-32 right-1/4 size-64 rounded-full bg-white/[0.07] blur-3xl"
          />

          <div className="relative grid grid-cols-1 items-center gap-7 lg:grid-cols-[minmax(0,1fr)_auto] lg:gap-10">
            <div>
              <span className="font-['Poppins',sans-serif] text-[10px] font-semibold uppercase tracking-[0.18em] text-indigo-100 sm:text-xs">
                Let's Build Something Better
              </span>

              <h3 className="mt-4 max-w-3xl font-['Poppins',sans-serif] text-2xl font-semibold leading-tight tracking-tight sm:text-3xl md:text-4xl lg:text-[2.65rem]">
                Ready to Start Your Project?
              </h3>

              <p className="mt-4 max-w-2xl text-sm leading-7 text-indigo-100 sm:text-base sm:leading-8">
                Building a new home, updating your interiors or repainting
                your property? Tell us what you need, and our team will
                help you plan the next step.
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-white/90 sm:text-sm">
                <span>Cube4Spaces</span>
                <span className="text-indigo-200">×</span>
                <span>Paint Studio</span>
              </div>
            </div>

            <a
              href="/contact/"
              className="group inline-flex min-h-12 w-full items-center justify-center gap-4 rounded-lg bg-white px-6 py-3 font-['Poppins',sans-serif] text-sm font-semibold text-indigo-700 transition duration-300 hover:-translate-y-0.5 hover:bg-indigo-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white sm:w-fit sm:min-w-[190px]"
            >
              <span>Get in Touch</span>

              <ArrowUpRight
                size={19}
                className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            </a>
          </div>
        </div>

        {/* Location and brand footer */}
        <div className="mt-8 flex flex-col gap-3 border-t border-slate-200 pt-6 text-xs leading-6 text-slate-500 sm:mt-10 sm:flex-row sm:items-center sm:justify-between sm:gap-5">
          <p>
            <span className="font-semibold text-slate-800">
              Cube4Spaces
            </span>
            {" "}— Interior Design &amp; Construction
          </p>

          <p>
            <span className="font-semibold text-slate-800">
              Paint Studio
            </span>
            {" "}— Interior &amp; Exterior Painting
          </p>

          <p>Serving Coimbatore and nearby areas.</p>
        </div>
      </div>
    </section>
  );
}
